import { Injectable } from '@angular/core';
import { QuestionState } from '../../enums/question-log';
import { ClickstreamService } from '../clickstream/clickstream.service';
import { ClickstreamEventTypeName } from '../../enums/clickstream-events';
import { FeatureFlagService } from 'src/app/modules/feature-flags/services/feature-flag/feature-flag.service';
import { FeatureFlags } from 'src/app/shared/enums/feature-flags';
import { MultipleChoiceTypes, TemplateID } from 'src/app/shared/enums';
import { EvaluatorFactory } from 'src/app/classes/evaluator-factory';
import {
  ImageData,
  Population,
  PopulationDNDGrid,
  PopulationDragAndDrop,
  PopulationHotText,
  TextData,
} from 'src/app/shared/interfaces';
import { PopulationService } from 'src/app/shared/services/population/population.service';
import {
  DnDGridEvaluator,
  DnDGridMatchEvaluator,
  DynamicDnDEvaluator,
  HotTextEvaluator,
} from 'src/app/classes';

@Injectable({
  providedIn: 'root',
})
export class QuestionLogService {
  readonly STRING_INNER_DELIMITER = '|';

  private preventClickstream = false;

  constructor(
    private clickstreamService: ClickstreamService,
    private featureFlagService: FeatureFlagService,
    private populationService: PopulationService
  ) {
    this.preventClickstream = !this.featureFlagService.isFlagEnabled(
      FeatureFlags.USE_CLICKSTREAM
    );
    this.featureFlagService.flagsChanged.subscribe(() => {
      this.preventClickstream = !this.featureFlagService.isFlagEnabled(
        FeatureFlags.USE_CLICKSTREAM
      );
    });
  }

  askDndBucketQuestion(
    questionId: string,
    questionPrompt: string,
    bucketLabels: string[],
    correctAnswers: string[][],
    discrims: string[],
    templateId: number,
    offset: number,
    passage?: TextData,
    image?: ImageData
  ): void {
    if (this.preventClickstream) {
      console.warn(
        `clickstream flag is off, we are not using the question log`
      );
      return;
    }

    const data = {
      questionId,
      questionPrompt,
      templateId,
      bucketLabels,
      correctAnswers,
      discrims,
    };
    if (passage) {
      Object.assign(data, { passage: passage.text });
    }
    if (image) {
      Object.assign(data, { image: image.url });
    }

    this.clickstreamService.addEventFromProperties(
      ClickstreamEventTypeName.Event,
      'Dnd Bucket Question',
      data,
      offset
    );
  }

  askOpenEndedQuestion(
    questionId: string,
    questionPrompt: string,
    templateId: number,
    offset: number,
    rubric?: string,
    wordCount?: number,
    passage?: TextData,
    image?: ImageData
  ): void {
    if (this.preventClickstream) {
      console.warn(
        `clickstream flag is off, we are not using the question log`
      );
      return;
    }

    const data = {
      questionId,
      questionPrompt,
      templateId,
    };
    if (rubric) {
      Object.assign(data, { rubric });
    }
    if (wordCount) {
      Object.assign(data, { wordCount });
    }
    if (passage) {
      Object.assign(data, { passage: passage.text });
    }
    if (image) {
      Object.assign(data, { image: image.url });
    }

    this.clickstreamService.addEventFromProperties(
      ClickstreamEventTypeName.Event,
      'Open Ended Question',
      data,
      offset
    );
  }

  askTextEntryQuestion(
    questionId: string,
    questionPrompt: string,
    correctAnswers: string[],
    templateId: number,
    offset: number,
    passage?: TextData,
    image?: ImageData
  ): void {
    if (this.preventClickstream) {
      console.warn(
        `clickstream flag is off, we are not using the question log`
      );
      return;
    }

    const data = {
      questionId,
      questionPrompt,
      templateId,
      correctAnswers,
    };
    if (passage) {
      Object.assign(data, { passage: passage.text });
    }
    if (image) {
      Object.assign(data, { image: image.url });
    }

    this.clickstreamService.addEventFromProperties(
      ClickstreamEventTypeName.Event,
      'Text Entry Question',
      data,
      offset
    );
  }

  askHotTextQuestion(
    questionId: string,
    question: string,
    correctAnswers: string | string[],
    isPractice: boolean,
    teiTemplateId: number,
    questionState: QuestionState,
    offset: number,
    passage: TextData,
    wordsSplit: string[]
  ): void {
    if (this.preventClickstream) {
      console.warn(
        `clickstream flag is off, we are not using the question log`
      );
      return;
    }
    const sortedCorrect = '';
    const correctAnswersIndex: number[] = [];
    wordsSplit?.forEach((word, index) => {
      if (
        correctAnswers.includes(
          HotTextEvaluator._formatAnswerWord(word).toLowerCase()
        )
      )
        correctAnswersIndex.push(index);
    });
    correctAnswers = Array.isArray(correctAnswers)
      ? correctAnswers.join(this.STRING_INNER_DELIMITER)
      : (correctAnswers as string);
    if (questionState === QuestionState.initial) {
      const data = [
        2, //event data format version
        !isPractice, //should this count towards the activity score?
        question, // question prompt
        passage.text, // promptPassage -> actual data from airtable
        wordsSplit, // split words used for calculation and answer matching
        correctAnswers, // list of all correct answers
        correctAnswersIndex, // list of all correct answers indexes
        teiTemplateId.toString(),
        questionId,
      ];
      this.clickstreamService.addEventFromProperties(
        ClickstreamEventTypeName.AskQuestion,
        sortedCorrect,
        data,
        offset
      );
    } else if (questionState === QuestionState.reAskQuestion) {
      this.clickstreamService.addEventFromProperties(
        ClickstreamEventTypeName.Event,
        'ReAskQuestion',
        sortedCorrect,
        offset
      );
    }
  }

  /**
   * Creates a DND_Grid Question event
   */
  askDndGridQuestion(
    questionId: string,
    questionPrompt: string,
    numCorrectAnswers: number,
    templateId: number,
    offset: number,
    standard?: string
  ): void {
    if (this.preventClickstream) {
      console.warn(
        `clickstream flag is off, we are not using the question log`
      );
      return;
    }

    const data = {
      questionId,
      templateId,
      prompt: questionPrompt,
      numCorrectAnswers,
      standard: standard ?? '',
    };

    this.clickstreamService.addEventFromProperties(
      ClickstreamEventTypeName.Event,
      'Dnd Grid Question',
      data,
      offset
    );
  }

  /**
   * Creates an askQuestion or reAsk Question event with given properties
   * @param question string The question prompt
   * @param correct string | string[] the correct answers
   * @param discrims string[] All potential answer choices, including distractors
   * @param isPractice boolean is this question practice?
   * @param teiTemplateId number TEI's TemplateID
   * @param shouldSort boolean Should the answer choices be sorted?
   * @returns void
   */
  askQuestion(
    questionId: string,
    question: string,
    correct: string | string[],
    discrims: string[],
    isPractice: boolean,
    teiTemplateId: number,
    shouldSort = true,
    questionState: QuestionState,
    offset: number,
    passage?: TextData,
    image?: ImageData
  ): void {
    if (this.preventClickstream) {
      console.warn(
        `clickstream flag is off, we are not using the question log`
      );
      return;
    }
    let sortedCorrect = '';
    if (shouldSort) {
      if (Array.isArray(correct)) {
        sortedCorrect = (this.sortAnswers(correct) as string[]).join(
          this.STRING_INNER_DELIMITER
        );
      } else {
        sortedCorrect = this.sortAnswers(correct) as string;
      }
    } else {
      sortedCorrect = Array.isArray(correct)
        ? correct.join(this.STRING_INNER_DELIMITER)
        : (correct as string);
    }
    if (questionState === QuestionState.initial) {
      const data = [
        2, //event data format version
        !isPractice, //should this count towards the activity score?
        question,
        discrims,
        teiTemplateId.toString(),
        questionId,
      ];
      if (passage) {
        data.push(passage.text);
      }
      if (image) {
        data.push(image.url);
      }
      this.clickstreamService.addEventFromProperties(
        ClickstreamEventTypeName.AskQuestion,
        sortedCorrect,
        data,
        offset
      );
    } else if (questionState === QuestionState.reAskQuestion) {
      this.clickstreamService.addEventFromProperties(
        ClickstreamEventTypeName.Event,
        'ReAskQuestion',
        sortedCorrect,
        offset
      );
    }
  }

  reAskQuestion(
    questionId: string,
    question: string,
    correct: string | string[],
    discrims: string[],
    isPractice: boolean,
    teiTemplateId: number,
    shouldSort = true,
    questionState: QuestionState,
    offset: number,
    passage?: TextData,
    image?: ImageData
  ): boolean {
    if (
      (MultipleChoiceTypes.includes(teiTemplateId) ||
        teiTemplateId === TemplateID.INLINE_CHOICE) &&
      teiTemplateId !== TemplateID.TRUE_FALSE_CHOICE
    ) {
      this.askQuestion(
        questionId,
        question,
        correct,
        discrims,
        isPractice,
        teiTemplateId,
        shouldSort,
        questionState,
        offset,
        passage,
        image
      );
      return true;
    }
    return false;
  }

  /**
   *  SORTING_BUCKETS
   *  SORTING_BUCKETS_IMAGES
   * @returns {boolean} true if question is correct
   */
  answerDndBucketQuestion(
    pop: Population,
    response: string[][],
    offset: number
  ): boolean {
    if (this.preventClickstream) {
      console.warn(
        `clickstream flag is off, we are not using the question log`
      );
      return false;
    }

    const isCorrect = DynamicDnDEvaluator.isCorrect(
      pop.populationData as PopulationDragAndDrop,
      response,
      false
    );

    this.clickstreamService.addEventFromProperties(
      ClickstreamEventTypeName.Event,
      'Dnd Bucket Answer',
      {
        questionId: pop.id ?? '',
        userResponse: response,
        isCorrect,
      },
      offset
    );

    return isCorrect;
  }

  /**
   * Records a DND_Grid Answer event
   * @returns {boolean} true if question is correct
   */
  answerDndGridQuestion(
    pop: Population,
    response: string[],
    attempt: number,
    offset: number
  ): boolean {
    if (this.preventClickstream) {
      console.warn(
        `clickstream flag is off, we are not using the question log`
      );
      return false;
    }

    let isCorrect = false;
    if (pop.templateID === TemplateID.DND_GRID) {
      isCorrect = DnDGridEvaluator.isCorrect(
        pop.populationData as PopulationDNDGrid,
        [response]
      );
    } else if (pop.templateID === TemplateID.DND_GRID_MATCH) {
      isCorrect = DnDGridMatchEvaluator.isCorrect(
        pop.populationData as PopulationDNDGrid,
        response
      );
    }

    const questionId = pop.id ?? '';
    const numAnswersDropped = response.length;
    const numCorrectAnswers = (pop.populationData as PopulationDNDGrid)
      .numCorrectAnswers;
    const answerState = isCorrect
      ? attempt === 0
        ? 'perfect'
        : 'correct'
      : 'incorrect';
    this.clickstreamService.addEventFromProperties(
      ClickstreamEventTypeName.Event,
      'Dnd Grid Answer',
      {
        questionId,
        userResponse: response,
        numAnswersDropped,
        numCorrectAnswers,
        answerState,
      },
      offset
    );

    return isCorrect;
  }

  /**
   *  CONSTRUCTED_RESPONSE
   *  CONSTRUCTED_RESPONSE_WITH_RUBRIC
   *  EXTENDED_CONSTRUCTED_RESPONSE
   *  EXTENDED_CONSTRUCTED_RESPONSE_WITH_RUBRIC
   */
  answerOpenEndedQuestion(
    questionId: string,
    response: string,
    offset: number
  ): void {
    if (this.preventClickstream) {
      console.warn(
        `clickstream flag is off, we are not using the question log`
      );
      return;
    }
    this.clickstreamService.addEventFromProperties(
      ClickstreamEventTypeName.Event,
      'Open Ended Answer',
      {
        questionId,
        userResponse: response,
      },
      offset
    );
  }

  /**
   *  TEXT_ENTRY
   * @returns {boolean} true if question is correct
   */
  answerTextEntryQuestion(
    questionId: string,
    response: string[],
    correct: string[],
    offset: number
  ): boolean {
    if (this.preventClickstream) {
      console.warn(
        `clickstream flag is off, we are not using the question log`
      );
      return false;
    }

    const isCorrect = correct.findIndex(c => c === response[0]) !== -1;

    this.clickstreamService.addEventFromProperties(
      ClickstreamEventTypeName.Event,
      `Text Entry Answer`,
      {
        questionId,
        userResponse: response,
        isCorrect,
      },
      offset
    );

    return isCorrect;
  }

  /**
   *  MULTIPLE_CHOICE string
   *  MULTIPLE_CHOICE_IMAGE string
   *  TRUE_FALSE_CHOICE string
   *  MULTI_SELECT array
   *  MULTI_SELECT_IMAGE array
   *  INLINE_CHOICE 2d array
   *  FILL_IN_THE_BLANK_TILE_TEXT
   * @returns {boolean} true if question is correct
   */
  // eslint-disable-next-line complexity
  answerQuestion(
    pop: Population,
    response: string[],
    shouldSort = false,
    state: QuestionState,
    offset: number
  ): boolean {
    if (this.preventClickstream) {
      console.warn(
        `clickstream flag is off, we are not using the question log`
      );
      return false;
    }

    let sortedResponse: string | string[] = '';
    if (shouldSort) {
      sortedResponse = this.sortAnswers(response);
    } else {
      sortedResponse = response;
    }

    // determine if correct with passed in answer ids from the user response
    const correct = EvaluatorFactory.isCorrect(
      pop.templateID,
      pop.populationData,
      [response]
    );

    // translate the passed in id array to the displayed answer for reporting
    let answers;
    if (Array.isArray(sortedResponse)) {
      answers = response.map(
        a => this.populationService.idToAnswerTextMap.get(a) ?? ''
      );
    } else {
      answers = sortedResponse;
    }

    if (state === QuestionState.askQuestion) {
      this.clickstreamService.addEventFromProperties(
        correct
          ? ClickstreamEventTypeName.AnswerCorrect
          : ClickstreamEventTypeName.AnswerIncorrect,
        Array.isArray(answers)
          ? answers.join(this.STRING_INNER_DELIMITER)
          : answers,
        [
          '2', //event data format version
        ],
        offset
      );
    } else if (state === QuestionState.reAskQuestion) {
      this.clickstreamService.addEventFromProperties(
        ClickstreamEventTypeName.Event,
        'ReAnswerQuestion',
        [
          '2',
          Array.isArray(response)
            ? response.join(this.STRING_INNER_DELIMITER)
            : response,
        ],
        offset
      );
    }

    return correct;
  }

  recordSkippedSelfRating(offset: number): void {
    this.clickstreamService.addEventFromProperties(
      ClickstreamEventTypeName.Event,
      'SkippedSelfRating',
      {},
      offset
    );
  }

  recordSelfRating(
    prompt: string,
    rating: string,
    offset: number,
    timeElapsed: number
  ): void {
    if (this.preventClickstream) {
      console.warn(
        `clickstream flag is off, we are not using the question log`
      );
      return;
    }
    this.clickstreamService.addEventFromProperties(
      ClickstreamEventTypeName.Event,
      'SelfRating',
      {
        questionPrompt: prompt,
        userResponse: rating,
        timeElapsed: timeElapsed,
      },
      offset
    );
  }

  private sortAnswers(answers: string | string[]): string | string[] {
    if (Array.isArray(answers)) {
      answers.forEach((answer, index) => {
        answers[index] = this.sortAnswers(answer) as string;
      });
      return answers.sort((a, b) => a.localeCompare(b));
    } else {
      if (answers.includes(this.STRING_INNER_DELIMITER)) {
        return answers
          .split(this.STRING_INNER_DELIMITER)
          .sort((a, b) => a.localeCompare(b))
          .join(this.STRING_INNER_DELIMITER);
      } else {
        return answers;
      }
    }
  }

  /**
   * @returns {boolean} true if question is correct
   */
  answerHotTextQuestion(
    population: PopulationHotText,
    questionId: string,
    response: string[],
    offset: number
  ): boolean {
    if (this.preventClickstream) {
      console.warn(
        `clickstream flag is off, we are not using the question log`
      );
      return false;
    }

    const isCorrect = HotTextEvaluator.isCorrect(population, [response]);

    this.clickstreamService.addEventFromProperties(
      isCorrect
        ? ClickstreamEventTypeName.AnswerCorrect
        : ClickstreamEventTypeName.AnswerIncorrect,
      `Hot Text Answer`,
      {
        questionId,
        userResponse: response,
        isCorrect,
      },
      offset
    );

    return isCorrect;
  }
}
